import { MeetingPrepInteractionResult, MeetingPrepResult } from '../../../../../models/MeetingPrep'
import { AIAgentResult, AIAgentType } from '../../../../../models/AIAgent'
import { MeetingPrepInteractionPayload } from '../../../../../views/AIAgent/hooks/useMeetingPrep'
import React, { useEffect, useMemo, useState } from 'react'
import { Col, Container, OverlayTrigger, Row, Stack } from 'react-bootstrap'

import { AIAgentResultCitations, Citation } from '../../../../../views/AIAgent/AIAgentResultCitations/AIAgentResultCitations'
import { AIAgentLoader } from '../../../../../views/AIAgent/AIAgentLoader/AIAgentLoader'
import { CitationNodeContext } from '../../MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNodeContext'
import { AIAgentResultState, EditableAIAgentResult, EditButtonComponentProps } from '../../../../../views/AIAgent/EditableAIAgentResult/EditableAIAgentResult'
import { LEXICAL_NODES } from '../../MeetingSummary/MeetingResults/Lexical/nodes'
import { CitationNode, getCitationNodesFromEditorState } from '../../MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNode'
import { getEditorStateFromHtml } from '../../MeetingSummary/MeetingResults/Lexical/utils'
import { nanoid } from 'nanoid'
import { TrackEvent, useTracking } from '../../common/tracking'
import { Button } from '../../../../../components/Button/Button'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import { useAIAgentResultCopy } from '../../../../../views/AIAgent/hooks/useAIAgentResultCopy'
import { useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { selectSelectedMeetingTemplate, selectSessionMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { getRoomTrackingDetails } from '../../tracking'
import { AIAgentLastUpdated } from '../../../../../views/AIAgent/AIAgentLastUpdated/AIAgentLastUpdated'
import { HeaderResultOptionsButton } from '../../MeetingSummary/MeetingResults/ResultCard/Header/HeaderResultOptions/HeaderResultOptionsButton'
import { Agenda } from '../Agenda/Agenda'
import { useSelectedMeetingTemplateHasAgendaEnabled } from '../Agenda/useAgendaFeature'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { formatDateDDMMYYYY, formatDateHM, timeAgo } from '../../common/aifeatures/utils'
import { AutoMeetingPrepPromo } from '../AutoMeetingPrepPromo/AutoMeetingPrepPromo'
import { useAutoMeetingPrepMetadata } from '../../../../../views/Setup/Settings/AutoMeetingPrep/hooks'
import { isLiveSession } from '../../../../../common/session.util'

interface PrepProps {
  isLoading: boolean
  interactionIsLoading: boolean
  editIsLoading: boolean
  existingMeetingPrep: AIAgentResult | null
  meetingPrep: MeetingPrepResult | null
  onMeetingPrepEditInteraction: (payload: MeetingPrepInteractionPayload) => Promise<MeetingPrepInteractionResult | undefined>
  onMeetingPrepExecution: (selectedMeetingTemplateId: string) => Promise<void>
  citations: Citation[] | null
  citationIsLoading: boolean
  agentMeetingTemplateId?: string
  exportPDFRef: React.MutableRefObject<null>
  exportPDFRefWithoutAgenda: React.MutableRefObject<null>
}

export const Prep: React.FC<PrepProps> = ({
  isLoading,
  interactionIsLoading,
  editIsLoading,
  existingMeetingPrep,
  meetingPrep,
  onMeetingPrepEditInteraction,
  onMeetingPrepExecution,
  citations = [],
  citationIsLoading,
  agentMeetingTemplateId,
  exportPDFRef,
  exportPDFRefWithoutAgenda,
}) => {
  const { tracked } = useTracking()
  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const [showRerunPrompt, setShowRerunPrompt] = useState<boolean>(false)
  const meetingTemplate = useAppSelector(selectSessionMeetingTemplate)
  const meetingTemplateId = meetingTemplate?.id

  const accountId = useAppSelector(selectAccountId)
  const isAgendaEnabledForSelectedMeetingTemplate = useSelectedMeetingTemplateHasAgendaEnabled()

  useEffect(() => {
    if (!agentMeetingTemplateId || !meetingTemplateId) {
      return
    }

    setShowRerunPrompt(agentMeetingTemplateId !== meetingTemplateId)
  }, [agentMeetingTemplateId, meetingTemplateId])

  const trackingEventDetails = {
    ...getRoomTrackingDetails(roomSession),
    selectedMeetingTemplateId: meetingTemplateId,
    source: isLiveSession(roomSession) ? 'live' : 'upcoming',
  }

  const meetingPrepDocumentState = useMemo<AIAgentResultState | null>(() => {
    if (!meetingPrep?.document) {
      return null
    }
    return {
      html: meetingPrep.document,
      editorState: getEditorStateFromHtml(meetingPrep.document, [...LEXICAL_NODES, CitationNode]),
      displayId: nanoid(),
    }
  }, [meetingPrep?.document])

  const meetingPrepEmailState = useMemo<AIAgentResultState | null>(() => {
    if (!meetingPrep?.email) {
      return null
    }
    return {
      html: meetingPrep.email,
      editorState: getEditorStateFromHtml(meetingPrep.email, [...LEXICAL_NODES, CitationNode]),
      displayId: nanoid(),
    }
  }, [meetingPrep?.email])

  const filteredCitations = useMemo(() => {
    if (!citations || !meetingPrepDocumentState || !meetingPrepEmailState) {
      return []
    }
    const documentCitationNodes = getCitationNodesFromEditorState(meetingPrepDocumentState.editorState)
    const emailCitationNodes = getCitationNodesFromEditorState(meetingPrepEmailState.editorState)
    const nodeExistsForCitation = (citation: Citation) => [...documentCitationNodes, ...emailCitationNodes].find((e) => e.getCitationId() === citation.id)
    return citations.filter((citation) => nodeExistsForCitation(citation))
  }, [citations, meetingPrepDocumentState, meetingPrepEmailState])

  const { handleCopy: _handleDocumentCopy } = useAIAgentResultCopy({
    aiAgentResultState: meetingPrepDocumentState,
    lexicalNodes: [...LEXICAL_NODES, CitationNode],
  })
  const { handleCopy: _handleEmailCopy, handleMailToClicked: _handleMailToClicked } = useAIAgentResultCopy({
    aiAgentResultState: meetingPrepEmailState,
    lexicalNodes: [...LEXICAL_NODES, CitationNode],
  })

  const { finishedAt: autoMeetingPrepFinishedAt } = useAutoMeetingPrepMetadata(roomSession)

  const _handleDocumentEditInteraction = async (editedMeetingPrepDocument: string) => {
    return await onMeetingPrepEditInteraction({ type: 'documentEdit', document: editedMeetingPrepDocument })
  }

  const handleDocumentEditInteraction = tracked(_handleDocumentEditInteraction, {
    eventName: TrackEvent.MEETING_PREP_INTERACTED_WITH,
    eventDetails: { ...trackingEventDetails, type: 'documentEdit' },
  })

  const _handleEmailEditInteraction = async (editedMeetingPrepEmail: string) => {
    return await onMeetingPrepEditInteraction({ type: 'emailEdit', email: editedMeetingPrepEmail })
  }

  const handleEmailEditInteraction = tracked(_handleEmailEditInteraction, {
    eventName: TrackEvent.MEETING_PREP_INTERACTED_WITH,
    eventDetails: { ...trackingEventDetails, type: 'emailEdit' },
  })

  const handleDocumentCopy = tracked(_handleDocumentCopy, {
    eventName: TrackEvent.MEETING_PREP_COPIED,
    eventDetails: trackingEventDetails,
  })

  const DocumentResultActionButtonsComponent = () => {
    return (
      <HeaderResultOptionsButton
        resultOptions={[
          {
            selectOption: {
              id: 'copy',
              label: 'Copy',
            },
            icon: 'bi bi-copy',
            handleSelected: handleDocumentCopy,
          },
        ]}
        disabled={false}
      />
    )
  }

  const editButtonComponent =
    (isEmail: boolean) =>
    ({ handleClick }: EditButtonComponentProps) => {
      return (
        <OverlayTrigger
          trigger={citationIsLoading ? ['hover', 'focus'] : []}
          placement='top'
          overlay={
            <BootstrapTooltip className='zocks-text-gray-500'>Editing is disabled while references are being generated. Please hold on.</BootstrapTooltip>
          }
        >
          <Button
            className='button-large p-2'
            variant='header'
            onClick={handleClick}
            disabled={(isEmail && roomSession?.isActive) || isLoading || interactionIsLoading || citationIsLoading || editIsLoading}
          >
            <i className='bi bi-pencil-square' />
            <span className='button-small'>Edit</span>
          </Button>
        </OverlayTrigger>
      )
    }

  const handleMailToClicked = tracked(_handleMailToClicked, {
    eventName: TrackEvent.MEETING_PREP_MAILTO_CLICKED,
    eventDetails: trackingEventDetails,
  })

  const handleEmailCopy = tracked(_handleEmailCopy, {
    eventName: TrackEvent.MEETING_PREP_EMAIL_COPIED,
    eventDetails: trackingEventDetails,
  })

  const EmailResultActionButtonsComponent = () => {
    return (
      <Stack direction='horizontal'>
        <Button className='button-large p-2' variant='primary' onClick={handleMailToClicked} disabled={!!roomSession?.isActive}>
          <i className='bi bi-send' />
          <span className='button-small'>Send</span>
        </Button>

        <HeaderResultOptionsButton
          resultOptions={[
            {
              selectOption: {
                id: 'copy',
                label: 'Copy',
              },
              icon: 'bi bi-copy',
              handleSelected: handleEmailCopy,
            },
          ]}
          disabled={!!roomSession?.isActive}
        />
      </Stack>
    )
  }

  if (!roomSession || !meetingTemplateId) {
    return null
  }

  if (isLoading) {
    return (
      <div className='position-relative'>
        <div className='position-absolute z-1 my-2 w-100'>
          <Container>
            <AutoMeetingPrepPromo />
          </Container>
        </div>
        <div className='position-relative'>
          <AIAgentLoader
            longLoadingMessage='Meeting Preparations with a large context might take up to 45 seconds to generate, please hold on'
            withChat={false}
          />
        </div>
      </div>
    )
  }

  const shouldShowAutoMeetingPrepBanner =
    existingMeetingPrep && autoMeetingPrepFinishedAt && new Date(existingMeetingPrep.createdAt) < new Date(autoMeetingPrepFinishedAt)

  const _handleRerun = async () => {
    if (meetingTemplateId) {
      await onMeetingPrepExecution(meetingTemplateId)
    }
  }

  const handleRerun = tracked(_handleRerun, {
    eventName: TrackEvent.MEETING_PREP_RERUN,
    eventDetails: trackingEventDetails,
  })

  const handleDismiss = () => {
    setShowRerunPrompt(false)
  }

  const HeaderComponent = () => {
    if (!selectedMeetingTemplate) {
      return <></>
    }
    return (
      <Row className='mx-0 pb-4'>
        <Col className='py-2 zocks-px-1.75 zocks-bg-color-primary-light'>
          <div className=' d-flex align-items-center justify-content-between'>
            <div className='d-flex gap-3 align-items-center'>
              <div className='d-flex gap-2 align-items-center'>
                <i className='bi bi-exclamation-circle' />
                <div className='paragraph-medium'>
                  The meeting type has changed. Do you want to rerun with the meeting type <span className='bold'>{selectedMeetingTemplate.name}</span>?
                </div>
              </div>
              <Button variant='primary' className='button-small p-1' onClick={handleRerun}>
                Rerun
              </Button>
            </div>
            <div>
              <Button variant='borderless' className='zocks-bg-color-primary-light' onClick={handleDismiss}>
                <i className='bi bi-x' />
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    )
  }

  return (
    <Container className='d-flex flex-column px-0 mx-0'>
      {showRerunPrompt ? <HeaderComponent /> : null}
      <Row className='mx-0'>
        <Col xs={12} className='px-0 d-flex flex-column gap-3'>
          {shouldShowAutoMeetingPrepBanner && autoMeetingPrepFinishedAt ? (
            <div className='d-flex p-3 gap-3 mb-3 align-items-center justify-content-center zocks-border-0.5 zocks-bg-color-gray-100 border border-1 zocks-border-color-gray-300'>
              <i className='bi bi-info-square fs-6' />
              <div className='paragraph-medium'>
                Auto-generated on {formatDateDDMMYYYY(autoMeetingPrepFinishedAt)} at {formatDateHM(autoMeetingPrepFinishedAt)} (
                <strong>{timeAgo(autoMeetingPrepFinishedAt)}</strong>
                ). Client information may have changed since then. Rerun to include the most current data in your prep.
              </div>
            </div>
          ) : null}
          <div ref={exportPDFRef} className='d-flex flex-column gap-3'>
            {isAgendaEnabledForSelectedMeetingTemplate ? (
              <Agenda accountId={accountId} roomId={roomSession.roomId} sessionId={roomSession.id} meetingTemplateId={meetingTemplateId} />
            ) : null}
            <div ref={exportPDFRefWithoutAgenda} className='d-flex flex-column gap-3 h-100 prep-container overflow-y-auto overflow-x-hidden'>
              {meetingPrepDocumentState ? (
                <CitationNodeContext.Provider value={{ citations: interactionIsLoading ? [] : filteredCitations }}>
                  <EditableAIAgentResult
                    label='Summary'
                    aiAgentResultState={meetingPrepDocumentState}
                    handleInteraction={handleDocumentEditInteraction}
                    isLoading={isLoading}
                    interactionIsLoading={interactionIsLoading || editIsLoading}
                    collapsible={true}
                    ActionButtonsComponent={DocumentResultActionButtonsComponent}
                    EditButtonComponent={editButtonComponent(false)}
                    lexicalNodes={[...LEXICAL_NODES, CitationNode]}
                  />
                </CitationNodeContext.Provider>
              ) : null}
            </div>
          </div>
          <div className='d-flex flex-column gap-3'>
            {meetingPrepEmailState ? (
              <CitationNodeContext.Provider value={{ citations: interactionIsLoading ? [] : filteredCitations }}>
                <EditableAIAgentResult
                  label='E-mail'
                  aiAgentResultState={meetingPrepEmailState}
                  handleInteraction={handleEmailEditInteraction}
                  isLoading={isLoading}
                  interactionIsLoading={interactionIsLoading || editIsLoading}
                  collapsible={true}
                  collapseByDefault={true}
                  ActionButtonsComponent={EmailResultActionButtonsComponent}
                  EditButtonComponent={editButtonComponent(true)}
                  lexicalNodes={[...LEXICAL_NODES, CitationNode]}
                />
              </CitationNodeContext.Provider>
            ) : null}
            <AIAgentResultCitations aiAgentType={AIAgentType.MEETING_PREPARATION} citationIsLoading={citationIsLoading} citations={filteredCitations} />
            <AIAgentLastUpdated existingAgentResult={existingMeetingPrep} />
          </div>
        </Col>
      </Row>
    </Container>
  )
}
